import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Smartphone, Share, Plus, Download, QrCode, ArrowLeft, ChevronRight, Chrome } from "lucide-react";

/**
 * Install landing page — shows platform-specific instructions for installing
 * the PWA to the user's home screen so it behaves like a downloadable App.
 *
 * - Android/Chrome/Edge: hooks the `beforeinstallprompt` event to show a real
 *   "Install App" button.
 * - iOS Safari: shows step-by-step Add-to-Home-Screen guide (iOS doesn't
 *   support programmatic install).
 * - Desktop: shows QR code that phones can scan to open this same page.
 */

function detectPlatform() {
  if (typeof window === "undefined") return "desktop";
  const ua = navigator.userAgent || "";
  const isIOS = /iPhone|iPad|iPod/i.test(ua) && !window.MSStream;
  const isAndroid = /Android/i.test(ua);
  if (isIOS) return "ios";
  if (isAndroid) return "android";
  return "desktop";
}

export default function Install() {
  const { t, i18n } = useTranslation();
  const platform = useMemo(detectPlatform, []);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [installed, setInstalled] = useState(false);

  // Detect if already installed (running as PWA)
  const [isStandalone, setIsStandalone] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(display-mode: standalone)");
    setIsStandalone(mq.matches || window.navigator.standalone === true);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const installedHandler = () => setInstalled(true);
    window.addEventListener("beforeinstallprompt", handler);
    window.addEventListener("appinstalled", installedHandler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const triggerInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === "accepted") setInstalled(true);
    setDeferredPrompt(null);
  };

  const pageUrl = typeof window !== "undefined" ? window.location.origin + "/install" : "";
  const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(pageUrl)}&size=280x280&margin=8&color=10151B`;

  const switchLang = () => {
    const next = i18n.language === "zh" ? "en" : "zh";
    i18n.changeLanguage(next);
    try { localStorage.setItem("lang", next); } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="h-14 px-4 sm:px-6 flex items-center justify-between border-b border-border/60">
        <Link to="/" data-testid="install-back" className="btn-swiss h-9 px-3 gap-1.5">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-xs">{t("back") || "返回"}</span>
        </Link>
        <button data-testid="install-lang" onClick={switchLang} className="btn-swiss h-9 px-3 text-xs">
          {i18n.language === "zh" ? "中文 / EN" : "EN / 中文"}
        </button>
      </header>

      <main className="max-w-2xl mx-auto px-5 sm:px-6 py-10 sm:py-14">
        <div className="mb-8 sm:mb-10">
          <div className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
            // 01 / INSTALL
          </div>
          <h1 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-tight">
            {t("install_title")}
          </h1>
          <p className="mt-3 text-sm sm:text-base text-muted-foreground max-w-lg">
            {t("install_subtitle")}
          </p>
        </div>

        {isStandalone && (
          <div
            data-testid="install-already"
            className="border border-primary/40 bg-primary/5 px-5 py-4 rounded-sm mb-8"
          >
            <div className="font-semibold text-sm">{t("install_already")}</div>
            <div className="text-xs text-muted-foreground mt-1">{t("install_already_hint")}</div>
          </div>
        )}

        {/* Platform-specific block */}
        {platform === "android" && (
          <AndroidBlock
            canPrompt={!!deferredPrompt}
            installed={installed}
            onInstall={triggerInstall}
            t={t}
          />
        )}
        {platform === "ios" && <IosBlock t={t} />}
        {platform === "desktop" && (
          <DesktopBlock qrSrc={qrSrc} pageUrl={pageUrl} t={t} />
        )}

        {/* Small print / troubleshooting */}
        <div className="mt-10 pt-8 border-t border-border/60 text-xs text-muted-foreground space-y-2">
          <div className="font-mono uppercase tracking-widest text-[10px] mb-2">
            {t("install_troubleshoot")}
          </div>
          <div>• {t("install_note_chrome")}</div>
          <div>• {t("install_note_ios")}</div>
          <div>• {t("install_note_offline")}</div>
        </div>
      </main>
    </div>
  );
}

function StepRow({ index, icon: Icon, title, body }) {
  return (
    <div className="flex gap-4 py-4 border-b border-border/60 last:border-b-0">
      <div className="w-8 h-8 flex-shrink-0 rounded-md border-2 border-[hsl(var(--brand))] bg-white flex items-center justify-center font-mono text-xs font-bold text-[hsl(var(--brand))]">
        {index}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 font-semibold text-sm">
          {Icon && <Icon className="w-4 h-4" strokeWidth={1.75} />}
          <span>{title}</span>
        </div>
        {body && <div className="text-xs text-muted-foreground mt-1 leading-relaxed">{body}</div>}
      </div>
    </div>
  );
}

function AndroidBlock({ canPrompt, installed, onInstall, t }) {
  return (
    <div>
      <div className="border border-border rounded-sm p-5 mb-4 flex items-center gap-4">
        <Smartphone className="w-10 h-10 text-primary flex-shrink-0" strokeWidth={1.25} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{t("install_android_title")}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{t("install_android_desc")}</div>
        </div>
      </div>
      {installed ? (
        <div data-testid="install-installed" className="border border-primary/40 bg-primary/5 p-4 rounded-sm text-sm font-semibold">
          ✓ {t("install_done")}
        </div>
      ) : canPrompt ? (
        <button
          data-testid="install-btn"
          onClick={onInstall}
          className="btn-swiss primary w-full h-12 gap-2"
        >
          <Download className="w-4 h-4" strokeWidth={1.75} />
          <span className="text-sm font-semibold uppercase tracking-widest">{t("install_button")}</span>
        </button>
      ) : (
        <div>
          <StepRow index="1" icon={Chrome} title={t("android_step1_title")} body={t("android_step1_desc")} />
          <StepRow index="2" icon={ChevronRight} title={t("android_step2_title")} body={t("android_step2_desc")} />
          <StepRow index="3" icon={Plus} title={t("android_step3_title")} body={t("android_step3_desc")} />
        </div>
      )}
    </div>
  );
}

function IosBlock({ t }) {
  return (
    <div>
      <div className="border border-border rounded-sm p-5 mb-4 flex items-center gap-4">
        <Smartphone className="w-10 h-10 text-primary flex-shrink-0" strokeWidth={1.25} />
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm">{t("install_ios_title")}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{t("install_ios_desc")}</div>
        </div>
      </div>
      <StepRow index="1" icon={Share} title={t("ios_step1_title")} body={t("ios_step1_desc")} />
      <StepRow index="2" icon={Plus} title={t("ios_step2_title")} body={t("ios_step2_desc")} />
      <StepRow index="3" title={t("ios_step3_title")} body={t("ios_step3_desc")} />
    </div>
  );
}

function DesktopBlock({ qrSrc, pageUrl, t }) {
  return (
    <div className="border border-border rounded-sm p-5 sm:p-6 flex flex-col sm:flex-row gap-6 items-center">
      <div className="flex-shrink-0">
        <img
          data-testid="install-qr"
          src={qrSrc}
          alt="QR code"
          className="w-56 h-56 border border-border bg-white p-2"
        />
      </div>
      <div className="min-w-0 flex-1 text-center sm:text-left">
        <QrCode className="w-6 h-6 mx-auto sm:mx-0 mb-3 text-muted-foreground" strokeWidth={1.5} />
        <div className="font-semibold text-lg mb-2">{t("desktop_qr_title")}</div>
        <div className="text-sm text-muted-foreground mb-4">{t("desktop_qr_desc")}</div>
        <div className="font-mono text-[11px] break-all bg-muted/40 px-2 py-1.5 rounded-sm">
          {pageUrl}
        </div>
      </div>
    </div>
  );
}
